/**
 * Client-side auth field validation for the login page.
 * Password is never collected here (OIDC PKCE → Keycloak).
 *
 * Login hint is domain-agnostic:
 * - bare username → sent as-is (`derickdm`) — Keycloak username
 * - full email → sent as-is (`anyone@any-domain`)
 * - Lab-style → sent as-is (`point@20`)
 * - Lab + accidental host suffix → `point@20@host.tld` → `point@20`
 *
 * No hardcoded email domain — provision domain can change per env.
 */

export type UsernameIssue = 'required' | 'too-short' | 'invalid'

/** Letters, digits, and common username punctuation including `@`. */
const USERish = /^[a-zA-Z0-9._@-]{3,128}$/

/**
 * Optional host after a Lab username: `local@tag@host.tld` → `local@tag`.
 * Only strips when the trailing segment looks like a hostname (has a dot).
 */
const LAB_PLUS_HOST = /^([^@]+@[^@]+)@([a-z0-9.-]+\.[a-z]{2,})$/i

/** Map the typed login field to the Keycloak username / login_hint. */
export function normalizeUsername(raw: string): string {
  const value = raw.trim().toLowerCase()
  if (!value) return value
  const dual = value.match(LAB_PLUS_HOST)
  if (dual) return dual[1]!
  return value
}

export function validateUsername(raw: string): UsernameIssue | null {
  const trimmed = raw.trim()
  if (!trimmed) return 'required'
  if (trimmed.length < 3) return 'too-short'
  if (trimmed.length > 128) return 'invalid'
  if (!USERish.test(trimmed)) return 'invalid'

  const value = normalizeUsername(trimmed)
  if (value.length < 3) return 'too-short'
  if (value.length > 254) return 'invalid'
  if (!USERish.test(value)) return 'invalid'

  // At most one `@` on the Keycloak identity; both sides non-empty when present.
  const at = value.indexOf('@')
  if (at === 0 || at === value.length - 1) return 'invalid'
  if (at !== -1 && value.indexOf('@', at + 1) !== -1) return 'invalid'
  return null
}

export function usernameErrorMessage(issue: UsernameIssue): string {
  switch (issue) {
    case 'required':
      return 'Enter your username to continue.'
    case 'too-short':
      return 'Use at least 3 characters.'
    case 'invalid':
      return 'Enter a valid username.'
  }
}

/** Soft client lock after repeated invalid submits (credential-stuffing UX). */
export const LOGIN_ATTEMPT_LIMIT = 5
export const LOGIN_LOCK_MS = 30_000
